import { useCallback, useState } from 'react';
import { useApiCall } from './apiHelpers';

export type SpecificStatus = 'Draft' | 'Waiting_validate' | 'Recurring';

export interface CommunicationSearchItem {
  id: number;
  defTitle: string;
  defDescription: string | null;
  defSendStart: string | null;
  defSendEnd: string | null;
  defNotSendable: string | null;
  defChannelId: number | null;
  targetTitle: string | null;
  targetDescription: string | null;
  targetQueryId: number | null;
  targetCategoryId: number | null;
  creationConfigId: number | null;
  creationPersonalization: unknown | null;
  creationPayload: Record<string, unknown> | null;
  offerInputs: string[];
  isValidated: number | null;
  actionId: number | null;
  action: {
    actionStatusId: number | null;
  } | null;
  entityId: number | null;
  validUntil: string | null;
  lockDate: string | null;
  creationDate: string;
}

export interface CommunicationSearchFilters {
  allStores: boolean;
  entityId: string | number | null;
  defTitle: string;
  communicationInputId: string;
  creationDateFrom: string;
  creationDateTo: string;
  defChannelIds: Array<number | null>;
  specificStatuses: SpecificStatus[];
  actionStatusIds: number[];
}

interface CommunicationSearchResponse {
  totalItems?: number;
  member?: CommunicationSearchItem[];
}

export const DEFAULT_ITEMS_PER_PAGE = 12;

const buildSearchParams = (
  filters: CommunicationSearchFilters,
  page: number,
  itemsPerPage: number | 'all'
): URLSearchParams => {
  const params = new URLSearchParams();
  params.set('page', String(page));

  if (itemsPerPage === 'all') {
    params.set('pagination', 'false');
  } else {
    params.set('itemsPerPage', String(itemsPerPage));
  }

  if (!filters.allStores && filters.entityId) {
    params.set('entityId', String(filters.entityId));
  }
  if (filters.defTitle) params.set('defTitle', filters.defTitle);
  if (filters.communicationInputId) params.set('communicationInputId', filters.communicationInputId);
  if (filters.creationDateFrom) params.set('creationDate[after]', filters.creationDateFrom);
  if (filters.creationDateTo) params.set('creationDate[before]', filters.creationDateTo);

  if (filters.defChannelIds.length > 0) {
    params.set('defChannelId', filters.defChannelIds.map((id) => (id === null ? 'null' : String(id))).join(','));
  }
  if (filters.specificStatuses.length > 0) {
    params.set('specificStatus', filters.specificStatuses.join(','));
  }
  if (filters.actionStatusIds.length > 0) {
    params.set('actionStatusId', filters.actionStatusIds.join(','));
  }

  return params;
};

const useSearchCommunications = () => {
  const { authFetch, isReady, run, isLoading, error } = useApiCall();
  const [communications, setCommunications] = useState<CommunicationSearchItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);

  const search = useCallback(
    async (filters: CommunicationSearchFilters, page = 1, itemsPerPage: number | 'all' = DEFAULT_ITEMS_PER_PAGE): Promise<void> => {
      if (!isReady) return;
      const params = buildSearchParams(filters, page, itemsPerPage);
      const result = await run(
        () =>
          authFetch(`/communication/getCommunicationsByFilter?${params.toString()}`, {
            method: 'GET',
          }) as Promise<CommunicationSearchResponse>,
        'Erreur lors de la recherche des campagnes',
      );
      if (result.ok) {
        setCommunications(result.data.member ?? []);
        setTotalItems(result.data.totalItems ?? 0);
      }
    },
    [authFetch, isReady, run],
  );

  return { communications, totalItems, isLoading, error, search };
};

export default useSearchCommunications;
